import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  effect,
  inject,
  input,
  resource,
  viewChild,
} from '@angular/core';
import { LoadingIndicatorComponent } from '@frontendlib/components/components/loading-indicator/loading-indicator.component';
import { PreviewErrorComponent } from '../preview-error/preview-error.component';
import { TopProgressService } from '@modules/top-progress/top-progress.service';

type PptxRendererModule = typeof import('@aiden0z/pptx-renderer');
type PptxViewer = InstanceType<PptxRendererModule['PptxViewer']>;

interface RenderParams {
  readonly blob: Blob | null;
  readonly container: HTMLDivElement | undefined;
}

@Component({
  selector: 'app-presentation-preview',
  templateUrl: './presentation-preview.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [LoadingIndicatorComponent, PreviewErrorComponent],
  host: { class: 'block w-full h-full' },
})
export class PresentationPreviewComponent {
  public readonly blob = input<Blob | null>(null);

  private readonly container = viewChild<ElementRef<HTMLDivElement>>('container');
  private currentViewer: PptxViewer | null = null;
  private readonly topProgress = inject(TopProgressService);

  protected readonly render = resource<boolean, RenderParams>({
    params: () => ({ blob: this.blob(), container: this.container()?.nativeElement }),
    defaultValue: false,
    loader: async ({ params, abortSignal }) => {
      const { blob, container } = params;
      if (!blob || !container) {
        return false;
      }

      const { PptxViewer } = await this.topProgress.track(import('@aiden0z/pptx-renderer'));
      abortSignal.throwIfAborted();

      this.disposeCurrentViewer();
      container.replaceChildren();

      this.currentViewer = await PptxViewer.open(blob, container, {
        renderMode: 'list',
        fitMode: 'contain',
        listOptions: { windowed: true, batchSize: 8 },
        signal: abortSignal,
        onSlideError: (index, error) => {
          console.warn(`[presentation-preview] slide ${index + 1} failed to render`, error);
        },
        onNodeError: (nodeId, error) => {
          console.warn(`[presentation-preview] node ${nodeId} failed to render`, error);
        },
      });

      return true;
    },
  });

  public constructor() {
    effect(() => {
      const err = this.render.error();
      if (err) {
        console.warn('[presentation-preview] render failed', err);
      }
    });
    inject(DestroyRef).onDestroy(() => this.disposeCurrentViewer());
  }

  private disposeCurrentViewer(): void {
    if (this.currentViewer) {
      try {
        this.currentViewer.destroy();
      } catch {
        // ignore teardown failures on partially-loaded viewers
      }
      this.currentViewer = null;
    }
  }
}
